import { PlanStoreyPolicy } from '@api/fastAPI.schemas'

export interface PlanSettingsFormValues {
  planStoreyPolicy: PlanStoreyPolicy
  storeysForPlans: string[]
  use_global_live_loads?: boolean
}

export const PLAN_STOREY_POLICY_OPTIONS: { value: PlanStoreyPolicy; label: string }[] = [
  { value: 'ElementStorey', label: 'Stockwerk des Elements' },
  { value: 'UserDefined', label: 'Benutzerdefiniert' },
]

export const DEFAULT_SLAB_LIKE_SETTINGS = {
  positionPlanSettings: {
    planStoreyPolicy: 'ElementStorey' as PlanStoreyPolicy,
    storeysForPlans: [] as string[],
    showLabel: true,
    showAssembly: true,
    labelPosition: null,
    labelOrientation: null,
  },
  certificateSettings: {} as SlabLikeCertificateSettings,
}

export const DEFAULT_LINE_LIKE_SETTINGS = {
  positionPlanSettings: {
    planStoreyPolicy: 'ElementStorey' as PlanStoreyPolicy,
    storeysForPlans: [] as string[],
    showLabel: true,
    showCrossSection: true,
    labelPosition: null,
    labelOrientation: null,
  },
  certificateSettings: {} as LineLikeCertificateSettings,
}

/** Plan settings to send when an element is (re-)created: its stored ones, or the defaults. */
export const lineLikeSettingsOf = (element?: {
  positionPlanSettings: LineLikePositionPlanSettings
  certificateSettings: LineLikeCertificateSettings
}) => ({
  positionPlanSettings:
    element?.positionPlanSettings ?? DEFAULT_LINE_LIKE_SETTINGS.positionPlanSettings,
  certificateSettings:
    element?.certificateSettings ?? DEFAULT_LINE_LIKE_SETTINGS.certificateSettings,
})

export const DEFAULT_WALL_SETTINGS = {
  positionPlanSettings: {
    planStoreyPolicy: 'ElementStorey' as PlanStoreyPolicy,
    storeysForPlans: [] as string[],
    showLabel: false,
    showAssembly: false,
    labelPosition: null,
    labelOrientation: null,
  },
  certificateSettings: {} as WallCertificateSettings,
}

export type WallSettings = Pick<Wall, 'positionPlanSettings' | 'certificateSettings'>

/** Plan settings to send when a wall is (re-)created: its stored ones, or the defaults. */
export const wallSettingsOf = (wall?: WallSettings): WallSettings => ({
  positionPlanSettings: wall?.positionPlanSettings ?? DEFAULT_WALL_SETTINGS.positionPlanSettings,
  certificateSettings: wall?.certificateSettings ?? DEFAULT_WALL_SETTINGS.certificateSettings,
})

/**
 * Elements whose plan storeys can be set: ceiling/roof positions, beams, purlins, columns, rips,
 * lintels and walls.
 */
export type PlanSettingsElement =
  | VerticalSlab
  | VerticalRoofSlab
  | PlanLineElement
  | Position
  | Wall

/** Rips and lintels live in the wall positions and are identified by their `position_guid`. */
export const isWallPosition = (element: PlanSettingsElement): element is Position =>
  element.element_type === 'rips' || element.element_type === 'lintels'

/** The model store types walls by their placement. */
export const isWall = (element: PlanSettingsElement): element is Wall =>
  element.element_type === 'inner_walls' || element.element_type === 'outer_walls'

export const planSettingsElementId = (element: PlanSettingsElement): string =>
  isWallPosition(element) ? element.position_guid : element.guid

export const defaultPlanSettingsValues = (
  element: PlanSettingsElement,
): PlanSettingsFormValues => ({
  planStoreyPolicy: element.positionPlanSettings.planStoreyPolicy,
  storeysForPlans: element.positionPlanSettings.storeysForPlans.map(storey => String(storey)),
  ...(element.element_type === 'vertical_slabs'
    ? { use_global_live_loads: element.metadata?.use_global_live_loads ?? true }
    : {}),
})

export const storeysForPlansToPersist = (values: PlanSettingsFormValues): string[] =>
  values.planStoreyPolicy === 'UserDefined' ? values.storeysForPlans : []
